import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { useCreateLocation, useUpdateLocation } from '../../hooks/use-locations';
import { locationFormSchema, LocationFormValues } from '../../schemas/location-form-schema';
import { Location } from '../../types/location.types';

export interface LocationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location?: Location | null;
}

export const LocationFormDialog = ({ open, onOpenChange, location }: LocationFormDialogProps) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(location);

  const { mutateAsync: createLocation, isPending: isCreating } = useCreateLocation();
  const { mutateAsync: updateLocation, isPending: isUpdating } = useUpdateLocation();
  const isSubmitting = isCreating || isUpdating;

  const form = useForm<LocationFormValues>({
    resolver: async (data, context, options) => {
      const result = await zodResolver(locationFormSchema)(data, context, options);
      if (result.errors) {
        result.errors = Object.fromEntries(
          Object.entries(result.errors).map(([key, error]) => [
            key,
            { ...error, message: t((error as { message?: string }).message ?? '') },
          ])
        );
      }
      return result;
    },
    defaultValues: { State: '' },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({ State: location?.State ?? '' });
  }, [open, location, form]);

  const handleSubmit = async (values: LocationFormValues) => {
    const input = values;
    try {
      if (isEditMode && location) {
        await updateLocation({ itemId: location.ItemId, input });
      } else {
        await createLocation(input);
      }
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditMode ? t('EDIT_LOCATION') : t('CREATE_LOCATION')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="State"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('LOCATION_STATE')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('LOCATION_STATE')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                {isEditMode ? t('SAVE_CHANGES') : t('CREATE')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
